import React from 'react'
import mapBoxGl from 'mapbox-gl'
import { Box } from '@mui/material'
import { MAPBOX_PUBLIC_TOKEN } from '../consts'

mapBoxGl.accessToken = MAPBOX_PUBLIC_TOKEN!

const layerId = 'layerId'
const routeLayerId = 'routeLayerId'

// Dieselbe Markenfarbe wie die Strecke im Kartenbild der Tourrechnung (getRouteMapImage).
const ROUTE_COLOR = '#7a2350'

// Ein runder Knopf in der Beschriftung; der Klick soll nicht bis zur Karte durchgehen.
const createRouteButton = ({
	content,
	title,
	style,
	onClick,
}: {
	content: string
	title: string
	style: string
	onClick: () => void
}): HTMLButtonElement => {
	const button = document.createElement('button')
	button.textContent = content
	button.title = title
	button.style.cssText =
		'display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;' +
		`padding:0;border:none;border-radius:50%;cursor:pointer;${style}`
	button.addEventListener('click', (event) => {
		event.stopPropagation()
		onClick()
	})
	return button
}

// Die Beschriftung auf der Strecke ist ein Mapbox-Marker und damit ein eigenes DOM-Element. Das
// mapbox-gl.css ist nicht geladen, deshalb steht position:absolute hier statt in .mapboxgl-marker.
const createRouteInfoElement = ({
	label,
	onClickInfo,
	marked,
	onToggleMark,
}: {
	label: string
	onClickInfo?: () => void
	marked: boolean
	onToggleMark?: () => void
}): HTMLElement => {
	const element = document.createElement('div')
	element.append(label)
	const hasButtons = onClickInfo || onToggleMark
	element.style.cssText =
		'position:absolute;top:0;left:0;z-index:1;display:flex;align-items:center;gap:6px;' +
		`padding:3px ${hasButtons ? 4 : 10}px 3px 10px;border-radius:14px;` +
		`border:2px solid ${ROUTE_COLOR};background:#fff;color:#231d1a;` +
		'font:600 12px/1.2 sans-serif;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,0.3)'
	if (onClickInfo) {
		element.append(
			createRouteButton({
				content: 'i',
				title: 'Tourrechnung anzeigen',
				style: `background:${ROUTE_COLOR};color:#fff;font:700 11px/1 Georgia,serif`,
				onClick: onClickInfo,
			})
		)
	}
	// Markenfarbe wie der Info-Knopf; blass mit grauer Birne, solange die Kombination nicht gemerkt ist.
	if (onToggleMark) {
		const button = createRouteButton({
			content: '',
			title: marked ? 'Aus den Kombis entfernen' : 'Als Kombi merken',
			style: `background:${ROUTE_COLOR};${marked ? '' : 'opacity:0.45'}`,
			onClick: onToggleMark,
		})
		const bulb = document.createElement('span')
		bulb.textContent = '💡'
		bulb.style.cssText = `font-size:11px;line-height:1;${
			marked ? '' : 'filter:grayscale(1) brightness(1.8)'
		}`
		button.append(bulb)
		element.append(button)
	}
	return element
}

// Der Punkt auf halber Länge der Strecke. Die Linie hat an Kurven viele Punkte und auf Geraden
// kaum welche, deshalb nicht einfach den mittleren Punkt; Längengrade schrumpfen mit cos(Breite).
const getRouteMidpoint = (coordinates: [number, number][]): [number, number] => {
	const lonScale = Math.cos((coordinates[0][1] * Math.PI) / 180)
	const segments = coordinates.slice(1).map((point, index) => {
		const previous = coordinates[index]
		return {
			from: previous,
			to: point,
			length: Math.hypot((point[0] - previous[0]) * lonScale, point[1] - previous[1]),
		}
	})
	const half = segments.reduce((sum, { length }) => sum + length, 0) / 2
	const { midpoint } = segments.reduce<{ walked: number; midpoint: [number, number] | null }>(
		(state, { from, to, length }) => {
			if (state.midpoint || state.walked + length < half) {
				return state.midpoint ? state : { ...state, walked: state.walked + length }
			}
			const ratio = length ? (half - state.walked) / length : 0
			return {
				walked: state.walked + length,
				midpoint: [from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio],
			}
		},
		{ walked: 0, midpoint: null }
	)
	return midpoint ?? coordinates[coordinates.length - 1]
}

// Ein Ausschnitt [[lon, lat], [lon, lat]], der alle Punkte zeigt; padding hält sie von Rändern und
// darübergelegten Panels frei.
export type MapBounds = {
	southWest: [number, number]
	northEast: [number, number]
	padding: { top: number; bottom: number; left: number; right: number }
}

const MapContainer = ({
	defaultLat = 51.0,
	defaultLon = 9.0,
	defaultZoom = 5.5,
	width,
	height,
	m,
	features,
	onClickMapFeature,
	focus,
	bounds,
	route,
	children,
}: {
	defaultLat?: number
	defaultLon?: number
	defaultZoom?: number
	width?: string
	height?: string
	m?: string
	features: {
		lat: number
		lon: number
		color: string
		id: string
		pointSize: number
		// Ein weißer Rand hebt einzelne Punkte hervor, etwa das ausgewählte Restaurant.
		strokeWidth?: number
	}[]
	onClickMapFeature: (id: string) => void
	focus?: { lat: number; lon: number; zoom: number }
	bounds?: MapBounds
	// Eine Strecke als [lon, lat]-Paare; in der Mitte steht label, optional mit Info-Knopf und
	// Glühbirne zum Merken der Kombination.
	route?: {
		coordinates: [number, number][]
		label: string
		onClickInfo?: () => void
		marked?: boolean
		onToggleMark?: () => void
	}
	children?: JSX.Element
}): JSX.Element => {
	const mapContainer = React.useRef<HTMLDivElement | null>(null)
	const map = React.useRef<mapBoxGl.Map | null>(null)
	const routeMarker = React.useRef<mapBoxGl.Marker | null>(null)

	// Die Klick-Handler werden einmal registriert und rufen immer den aktuellen Callback.
	const onClickMapFeatureRef = React.useRef(onClickMapFeature)
	onClickMapFeatureRef.current = onClickMapFeature
	const onClickRouteInfoRef = React.useRef(route?.onClickInfo)
	onClickRouteInfoRef.current = route?.onClickInfo
	const onToggleRouteMarkRef = React.useRef(route?.onToggleMark)
	onToggleRouteMarkRef.current = route?.onToggleMark

	const [loaded, setLoaded] = React.useState(false)

	const routeCoordinates = route?.coordinates
	const routeLabel = route?.label ?? ''
	const routeHasInfo = route?.onClickInfo !== undefined
	const routeHasMark = route?.onToggleMark !== undefined
	const routeMarked = route?.marked ?? false
	React.useEffect(() => {
		const routeSource = map.current?.getSource(routeLayerId) as mapBoxGl.GeoJSONSource | undefined
		if (loaded && routeSource) {
			routeSource.setData({
				type: 'FeatureCollection',
				features: routeCoordinates
					? [
							{
								type: 'Feature',
								geometry: { type: 'LineString', coordinates: routeCoordinates },
								properties: {},
							},
					  ]
					: [],
			})
			routeMarker.current?.remove()
			routeMarker.current = routeCoordinates?.length
				? new mapBoxGl.Marker({
						element: createRouteInfoElement({
							label: routeLabel,
							onClickInfo: routeHasInfo ? () => onClickRouteInfoRef.current?.() : undefined,
							marked: routeMarked,
							onToggleMark: routeHasMark ? () => onToggleRouteMarkRef.current?.() : undefined,
						}),
				  })
						.setLngLat(getRouteMidpoint(routeCoordinates))
						.addTo(map.current)
				: null
		}
	}, [loaded, routeCoordinates, routeHasInfo, routeHasMark, routeLabel, routeMarked])

	React.useEffect(() => {
		const geoGsonSource = map.current?.getSource(layerId) as mapBoxGl.GeoJSONSource | undefined
		if (loaded && geoGsonSource && features) {
			geoGsonSource.setData({
				type: 'FeatureCollection',
				features: features.map((f) => ({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [f.lon, f.lat],
					},
					properties: {
						'id': f.id,
						'marker-color': f.color,
						'marker-size': f.pointSize,
						'stroke-width': f.strokeWidth ?? 0,
					},
				})),
			})
		}
	}, [features, loaded])

	React.useEffect(() => {
		if (loaded && focus) {
			map.current.flyTo({ center: [focus.lon, focus.lat], zoom: focus.zoom, duration: 900 })
		}
	}, [focus, loaded])

	React.useEffect(() => {
		if (loaded && bounds) {
			map.current.fitBounds([bounds.southWest, bounds.northEast], {
				padding: bounds.padding,
				maxZoom: 11,
				duration: 900,
			})
		}
	}, [bounds, loaded])

	React.useEffect(() => {
		if (!map.current) {
			map.current = new mapBoxGl.Map({
				container: mapContainer.current,
				style: 'mapbox://styles/mapbox/light-v11',
				center: [defaultLon, defaultLat],
				zoom: defaultZoom,
			})
			map.current.on('load', () => {
				// Die Strecke zuerst, damit die Punkte über ihr liegen.
				map.current.addLayer({
					id: routeLayerId,
					type: 'line',
					source: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
					layout: { 'line-join': 'round', 'line-cap': 'round' },
					paint: { 'line-color': ROUTE_COLOR, 'line-width': 4, 'line-opacity': 0.9 },
				})
				map.current.addLayer({
					id: layerId,
					type: 'circle',
					source: {
						type: 'geojson',
						data: {
							type: 'FeatureCollection',
							features: [],
						},
					},
					paint: {
						'circle-radius': ['get', 'marker-size'],
						'circle-color': ['get', 'marker-color'],
						'circle-stroke-width': ['get', 'stroke-width'],
						'circle-stroke-color': '#ffffff',
					},
				})
				map.current.on('click', layerId, (e) => {
					onClickMapFeatureRef.current(e.features[0].properties.id)
				})
				setLoaded(true)
			})
		}
	}, [defaultLat, defaultLon, defaultZoom])

	return (
		<Box ref={mapContainer} m={m} height={height} width={width} position="relative">
			{children}
		</Box>
	)
}

export default MapContainer
